/**
 * Prompt templates hooks (Wave 3 b73) — tenant CRUD against /templates,
 * with the visibility filter that makes sysadmin-authored prompts
 * (visible_to_tenant=true) appear in the same list with an isSystem flag.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from './api-client';

export interface PromptVariable {
  name: string;
  label: string;
  type?: string;
  required?: boolean;
  default?: string;
  options?: string[];
}

export interface PromptTemplate {
  id: string;
  command: string;
  title: string;
  description: string | null;
  content: string;
  variables: PromptVariable[];
  category: string | null;
  isGlobal: boolean | null;
  /** True when the row is sysadmin-authored AND not owned by current tenant. */
  isSystem: boolean;
  visibleToTenant: boolean | null;
  /**
   * Round 3 fix #146 — when this row was cloned from a system_prompts
   * row via /templates/clone-from-system/:systemId, this carries the
   * source UUID. Null for tenant-original or system rows.
   */
  clonedFromSystemId: string | null;
}

export interface PromptListResponse {
  data: PromptTemplate[];
}

const KEYS = {
  list: ['prompt-templates'] as const,
};

export function usePromptTemplates() {
  return useQuery<PromptListResponse, Error>({
    queryKey: KEYS.list,
    queryFn: () => apiClient.get<PromptListResponse>('/templates'),
  });
}

export interface CreatePromptInput {
  command: string;
  title: string;
  description?: string;
  content: string;
  variables?: PromptVariable[];
  category?: string;
  isGlobal?: boolean;
}

export function useCreatePromptTemplate() {
  const qc = useQueryClient();
  return useMutation<{ data: PromptTemplate }, Error, CreatePromptInput>({
    mutationFn: (input) => apiClient.post<{ data: PromptTemplate }>('/templates', input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: KEYS.list });
    },
  });
}

export interface UpdatePromptInput {
  id: string;
  title?: string;
  description?: string;
  content?: string;
  variables?: PromptVariable[];
  category?: string;
  isGlobal?: boolean;
}

export function useUpdatePromptTemplate() {
  const qc = useQueryClient();
  return useMutation<{ data: PromptTemplate }, Error, UpdatePromptInput>({
    mutationFn: ({ id, ...body }) =>
      apiClient.request<{ data: PromptTemplate }>(`/templates/${id}`, {
        method: 'PUT',
        body: JSON.stringify(body),
        headers: { 'Content-Type': 'application/json' },
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: KEYS.list });
    },
  });
}

export function useDeletePromptTemplate() {
  const qc = useQueryClient();
  return useMutation<void, Error, { id: string }>({
    mutationFn: ({ id }) => apiClient.delete(`/templates/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: KEYS.list });
    },
  });
}

/**
 * Q3 prompt clone — POST /templates/clone-from-system/:systemId.
 *
 * Creates a tenant-owned editable prompt_templates row pointing at a
 * system_prompts source. The optional `command` overrides the auto-
 * generated tenant-local slash command; otherwise the server picks
 * one based on the source slug + collision suffix.
 */
export function useClonePromptFromSystem() {
  const qc = useQueryClient();
  return useMutation<{ data: PromptTemplate }, Error, { systemId: string; command?: string }>({
    mutationFn: ({ systemId, command }) =>
      apiClient.post<{ data: PromptTemplate }>(
        `/templates/clone-from-system/${systemId}`,
        command ? { command } : {},
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: KEYS.list });
    },
  });
}
